import { useParams, Link } from 'react-router-dom';
import { Button } from '@voilajsx/uikit/button';
import { Input } from '@voilajsx/uikit/input';
import { Textarea } from '@voilajsx/uikit/textarea';
import { Checkbox } from '@voilajsx/uikit/checkbox';
import { Switch } from '@voilajsx/uikit/switch';
import { Slider } from '@voilajsx/uikit/slider';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@voilajsx/uikit/card';
import { Badge } from '@voilajsx/uikit/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@voilajsx/uikit/avatar';
import { Progress } from '@voilajsx/uikit/progress';
import { Skeleton } from '@voilajsx/uikit/skeleton';
import { Alert, AlertDescription, AlertTitle } from '@voilajsx/uikit/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@voilajsx/uikit/tabs';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@voilajsx/uikit/accordion';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@voilajsx/uikit/tooltip';
import { CodePreview, Header } from '../../../shared/components';
import { ChevronLeft, AlertCircle, Info, CheckCircle, Sun } from 'lucide-react';

// Component configurations
const componentConfigs: Record<string, {
  title: string;
  description: string;
  preview: React.ReactNode;
  code: string;
  variants?: { name: string; preview: React.ReactNode; code: string }[];
}> = {
  button: {
    title: 'Button',
    description: 'Clickable buttons with multiple variants and sizes.',
    preview: (
      <div className="flex flex-wrap gap-3">
        <Button>Default</Button>
        <Button variant="secondary">Secondary</Button>
        <Button variant="outline">Outline</Button>
        <Button variant="ghost">Ghost</Button>
        <Button variant="destructive">Destructive</Button>
      </div>
    ),
    code: `import { Button } from '@voilajsx/uikit/button';

<Button>Default</Button>
<Button variant="secondary">Secondary</Button>
<Button variant="outline">Outline</Button>
<Button variant="ghost">Ghost</Button>
<Button variant="destructive">Destructive</Button>`,
    variants: [
      {
        name: 'Sizes',
        preview: (
          <div className="flex items-center gap-3">
            <Button size="sm">Small</Button>
            <Button size="default">Default</Button>
            <Button size="lg">Large</Button>
          </div>
        ),
        code: `<Button size="sm">Small</Button>
<Button size="default">Default</Button>
<Button size="lg">Large</Button>`,
      },
      {
        name: 'With Icons',
        preview: (
          <div className="flex gap-3">
            <Button><CheckCircle className="w-4 h-4 mr-2" />With Icon</Button>
            <Button size="icon"><Sun className="w-4 h-4" /></Button>
          </div>
        ),
        code: `<Button><CheckCircle className="w-4 h-4 mr-2" />With Icon</Button>
<Button size="icon"><Sun className="w-4 h-4" /></Button>`,
      },
    ],
  },
  input: {
    title: 'Input',
    description: 'Text input fields for user data entry.',
    preview: (
      <div className="space-y-3 max-w-sm">
        <Input placeholder="Default input" />
        <Input type="email" placeholder="Email address" />
        <Input type="password" placeholder="Password" />
        <Input disabled placeholder="Disabled input" />
      </div>
    ),
    code: `import { Input } from '@voilajsx/uikit/input';

<Input placeholder="Default input" />
<Input type="email" placeholder="Email address" />
<Input type="password" placeholder="Password" />
<Input disabled placeholder="Disabled input" />`,
  },
  textarea: {
    title: 'Textarea',
    description: 'Multi-line text input for longer content.',
    preview: (
      <div className="max-w-sm">
        <Textarea placeholder="Type your message here..." rows={4} />
      </div>
    ),
    code: `import { Textarea } from '@voilajsx/uikit/textarea';

<Textarea placeholder="Type your message here..." rows={4} />`,
  },
  checkbox: {
    title: 'Checkbox',
    description: 'Toggle checkboxes for boolean inputs.',
    preview: (
      <div className="space-y-3">
        <div className="flex items-center space-x-2">
          <Checkbox id="terms" />
          <label htmlFor="terms" className="text-sm">Accept terms and conditions</label>
        </div>
        <div className="flex items-center space-x-2">
          <Checkbox id="disabled" disabled />
          <label htmlFor="disabled" className="text-sm text-muted-foreground">Disabled checkbox</label>
        </div>
      </div>
    ),
    code: `import { Checkbox } from '@voilajsx/uikit/checkbox';

<div className="flex items-center space-x-2">
  <Checkbox id="terms" />
  <label htmlFor="terms">Accept terms and conditions</label>
</div>`,
  },
  switch: {
    title: 'Switch',
    description: 'Toggle switches for on/off states.',
    preview: (
      <div className="space-y-3">
        <div className="flex items-center space-x-2">
          <Switch id="airplane" />
          <label htmlFor="airplane" className="text-sm">Airplane Mode</label>
        </div>
        <div className="flex items-center space-x-2">
          <Switch id="disabled" disabled />
          <label htmlFor="disabled" className="text-sm text-muted-foreground">Disabled switch</label>
        </div>
      </div>
    ),
    code: `import { Switch } from '@voilajsx/uikit/switch';

<div className="flex items-center space-x-2">
  <Switch id="airplane" />
  <label htmlFor="airplane">Airplane Mode</label>
</div>`,
  },
  slider: {
    title: 'Slider',
    description: 'Range slider for numeric value selection.',
    preview: (
      <div className="max-w-sm space-y-6">
        <Slider defaultValue={[50]} max={100} step={1} />
        <Slider defaultValue={[25, 75]} max={100} step={1} />
      </div>
    ),
    code: `import { Slider } from '@voilajsx/uikit/slider';

<Slider defaultValue={[50]} max={100} step={1} />
<Slider defaultValue={[25, 75]} max={100} step={1} />`,
  },
  card: {
    title: 'Card',
    description: 'Content containers with header, content, and footer.',
    preview: (
      <Card className="max-w-sm">
        <CardHeader>
          <CardTitle>Card Title</CardTitle>
          <CardDescription>Card description goes here</CardDescription>
        </CardHeader>
        <CardContent>
          <p>This is the main content of the card.</p>
        </CardContent>
        <CardFooter>
          <Button>Action</Button>
        </CardFooter>
      </Card>
    ),
    code: `import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '@voilajsx/uikit/card';

<Card>
  <CardHeader>
    <CardTitle>Card Title</CardTitle>
    <CardDescription>Card description goes here</CardDescription>
  </CardHeader>
  <CardContent>
    <p>This is the main content of the card.</p>
  </CardContent>
  <CardFooter>
    <Button>Action</Button>
  </CardFooter>
</Card>`,
  },
  badge: {
    title: 'Badge',
    description: 'Small status indicators and labels.',
    preview: (
      <div className="flex flex-wrap gap-2">
        <Badge>Default</Badge>
        <Badge variant="secondary">Secondary</Badge>
        <Badge variant="outline">Outline</Badge>
        <Badge variant="destructive">Destructive</Badge>
      </div>
    ),
    code: `import { Badge } from '@voilajsx/uikit/badge';

<Badge>Default</Badge>
<Badge variant="secondary">Secondary</Badge>
<Badge variant="outline">Outline</Badge>
<Badge variant="destructive">Destructive</Badge>`,
  },
  avatar: {
    title: 'Avatar',
    description: 'User avatars with image and fallback support.',
    preview: (
      <div className="flex gap-4">
        <Avatar>
          <AvatarImage src="https://github.com/shadcn.png" />
          <AvatarFallback>CN</AvatarFallback>
        </Avatar>
        <Avatar>
          <AvatarFallback>JD</AvatarFallback>
        </Avatar>
        <Avatar className="w-12 h-12">
          <AvatarFallback className="text-lg">AB</AvatarFallback>
        </Avatar>
      </div>
    ),
    code: `import { Avatar, AvatarImage, AvatarFallback } from '@voilajsx/uikit/avatar';

<Avatar>
  <AvatarImage src="https://github.com/shadcn.png" />
  <AvatarFallback>CN</AvatarFallback>
</Avatar>`,
  },
  progress: {
    title: 'Progress',
    description: 'Progress indicators for loading states.',
    preview: (
      <div className="space-y-4 max-w-sm">
        <Progress value={33} />
        <Progress value={66} />
        <Progress value={100} />
      </div>
    ),
    code: `import { Progress } from '@voilajsx/uikit/progress';

<Progress value={33} />
<Progress value={66} />
<Progress value={100} />`,
  },
  skeleton: {
    title: 'Skeleton',
    description: 'Loading placeholders for content.',
    preview: (
      <div className="flex items-center space-x-4">
        <Skeleton className="h-12 w-12 rounded-full" />
        <div className="space-y-2">
          <Skeleton className="h-4 w-[250px]" />
          <Skeleton className="h-4 w-[200px]" />
        </div>
      </div>
    ),
    code: `import { Skeleton } from '@voilajsx/uikit/skeleton';

<div className="flex items-center space-x-4">
  <Skeleton className="h-12 w-12 rounded-full" />
  <div className="space-y-2">
    <Skeleton className="h-4 w-[250px]" />
    <Skeleton className="h-4 w-[200px]" />
  </div>
</div>`,
  },
  alert: {
    title: 'Alert',
    description: 'Alert messages for user notifications.',
    preview: (
      <div className="space-y-4 max-w-md">
        <Alert>
          <Info className="w-4 h-4" />
          <AlertTitle>Information</AlertTitle>
          <AlertDescription>This is an informational message.</AlertDescription>
        </Alert>
        <Alert variant="destructive">
          <AlertCircle className="w-4 h-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>Something went wrong.</AlertDescription>
        </Alert>
      </div>
    ),
    code: `import { Alert, AlertTitle, AlertDescription } from '@voilajsx/uikit/alert';

<Alert>
  <Info className="w-4 h-4" />
  <AlertTitle>Information</AlertTitle>
  <AlertDescription>This is an informational message.</AlertDescription>
</Alert>

<Alert variant="destructive">
  <AlertCircle className="w-4 h-4" />
  <AlertTitle>Error</AlertTitle>
  <AlertDescription>Something went wrong.</AlertDescription>
</Alert>`,
  },
  tabs: {
    title: 'Tabs',
    description: 'Tabbed content navigation.',
    preview: (
      <Tabs defaultValue="account" className="max-w-md">
        <TabsList>
          <TabsTrigger value="account">Account</TabsTrigger>
          <TabsTrigger value="password">Password</TabsTrigger>
        </TabsList>
        <TabsContent value="account" className="p-4">
          <p className="text-sm text-muted-foreground">Manage your account settings.</p>
        </TabsContent>
        <TabsContent value="password" className="p-4">
          <p className="text-sm text-muted-foreground">Change your password here.</p>
        </TabsContent>
      </Tabs>
    ),
    code: `import { Tabs, TabsList, TabsTrigger, TabsContent } from '@voilajsx/uikit/tabs';

<Tabs defaultValue="account">
  <TabsList>
    <TabsTrigger value="account">Account</TabsTrigger>
    <TabsTrigger value="password">Password</TabsTrigger>
  </TabsList>
  <TabsContent value="account">
    <p>Manage your account settings.</p>
  </TabsContent>
  <TabsContent value="password">
    <p>Change your password here.</p>
  </TabsContent>
</Tabs>`,
  },
  accordion: {
    title: 'Accordion',
    description: 'Collapsible content sections.',
    preview: (
      <Accordion type="single" collapsible className="max-w-md">
        <AccordionItem value="item-1">
          <AccordionTrigger>Is it accessible?</AccordionTrigger>
          <AccordionContent>
            Yes. It adheres to the WAI-ARIA design pattern.
          </AccordionContent>
        </AccordionItem>
        <AccordionItem value="item-2">
          <AccordionTrigger>Is it styled?</AccordionTrigger>
          <AccordionContent>
            Yes. It comes with default styles that match your theme.
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    ),
    code: `import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '@voilajsx/uikit/accordion';

<Accordion type="single" collapsible>
  <AccordionItem value="item-1">
    <AccordionTrigger>Is it accessible?</AccordionTrigger>
    <AccordionContent>
      Yes. It adheres to the WAI-ARIA design pattern.
    </AccordionContent>
  </AccordionItem>
</Accordion>`,
  },
  tooltip: {
    title: 'Tooltip',
    description: 'Hover tooltips for additional information.',
    preview: (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button variant="outline">Hover me</Button>
          </TooltipTrigger>
          <TooltipContent>
            <p>This is a tooltip</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    ),
    code: `import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@voilajsx/uikit/tooltip';

<TooltipProvider>
  <Tooltip>
    <TooltipTrigger asChild>
      <Button variant="outline">Hover me</Button>
    </TooltipTrigger>
    <TooltipContent>
      <p>This is a tooltip</p>
    </TooltipContent>
  </Tooltip>
</TooltipProvider>`,
  },
};

const ComponentPage = () => {
  const { component } = useParams<{ component: string }>();

  const config = component ? componentConfigs[component.toLowerCase()] : null;

  if (!config) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <h1 className="text-4xl font-bold mb-4">Component Not Found</h1>
            <p className="text-muted-foreground mb-6">
              The component "{component}" doesn't exist in the sandbox.
            </p>
            <Link to="/sandbox">
              <Button>Back to Sandbox</Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />

      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Back button */}
        <Link to="/sandbox" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-6">
          <ChevronLeft className="w-4 h-4" />
          Back to all components
        </Link>

        {/* Component header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2">{config.title}</h1>
          <p className="text-lg text-muted-foreground">{config.description}</p>
        </div>

        {/* Main preview */}
        <section className="mb-12">
          <h2 className="text-xl font-semibold mb-4">Preview</h2>
          <Card>
            <CardContent className="p-8 flex items-center justify-center min-h-[200px] bg-muted/20">
              {config.preview}
            </CardContent>
          </Card>
        </section>

        {/* Code */}
        <section className="mb-12">
          <h2 className="text-xl font-semibold mb-4">Code</h2>
          <CodePreview code={config.code} language="tsx" />
        </section>

        {/* Variants */}
        {config.variants && config.variants.length > 0 && (
          <section className="mb-12">
            <h2 className="text-xl font-semibold mb-4">Variants</h2>
            <div className="space-y-8">
              {config.variants.map((variant, index) => (
                <div key={index}>
                  <h3 className="text-lg font-medium mb-3">{variant.name}</h3>
                  <Card className="mb-4">
                    <CardContent className="p-6 flex items-center justify-center bg-muted/20">
                      {variant.preview}
                    </CardContent>
                  </Card>
                  <CodePreview code={variant.code} language="tsx" />
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Import */}
        <section className="mb-12">
          <h2 className="text-xl font-semibold mb-4">Import</h2>
          <CodePreview
            code={`import { ${config.title} } from '@voilajsx/uikit/${config.title.toLowerCase()}';`}
            language="tsx"
          />
        </section>
      </main>
    </div>
  );
};

export default ComponentPage;
